import { useCallback, useEffect } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "./config/firebaseConfig.js";
import { HelmetProvider } from "react-helmet-async";
import "./assets/sass/style.scss";
import "./app.css";

import {
  RegisterLayout,
  SchoolRegisterForm,
  IndividualRegisterForm,
  Onboarding,
  Step1,
  Step3,
  Step4,
  LoginLayout,
  UserLogin,
  ForgotPassword,
  ExamLogin,
} from "./pages/auth";

import {
  ParentDashboard,
  ParentDashboardLayout,
  RegisteredStudents,
  SchoolDashboard,
  SchoolDashboardLayout,
  StudentLayout,
} from "./pages/dashboard";

import {
  LandingLayout,
  StudentPage,
  ParentPage,
  EducatorPage,
  OlympiadLanding,
  AboutPage,
  Terms,
  PrivacyPolicy,
  ContactLanding,
  FaqLanding,
  SyllabusLanding,
  ExamLanding,
  AwardsLanding,
  CommingSoon,
  InstructionsLanding,
  OurApps,
} from "./pages/landingPage";

import {
  ContentCreationLayout,
  AllQuestions,
  UploadQuestion,
} from "./pages/contentCreation";

import { ExamInstructions, ExamPage, Setup } from "./pages/examModule";

import PageNotFound from "./pages/PageNotFound.jsx";
import SchoolLandingPage from "./pages/landingPage/SchoolPage.jsx";
import DashobardLinks from "./pages/dashboard/DashobardLinks.jsx";
import PasswordModule from "./pages/auth/register/school/PasswordModule.jsx";
import usePageViews from "./utils/hooks/UsePageViews.js";
import UserAuthProtected from "./utils/AuthLayout.jsx";
import { login } from "./redux/features/authSlice.js";
import ExamAuthProtected from "./utils/ExamAuhLayout.jsx";
import SchoolRegisterFormStep from "./pages/auth/register/school/SchoolRegisterFormStep.jsx";



const App = () => {
  usePageViews();
  const dispatch = useDispatch();

  // fetch user login details
  const [userAuth, authLoading] = useAuthState(auth);
  const fetchAdminDetails = useCallback(() => {
    try {
      dispatch(login(""))
        .unwrap()
        .catch((error) => {
          console.log(error);
        });
    } catch (err) {
      console.log(err);
    }
  }, [dispatch]);

  useEffect(() => {
    const unsub = !authLoading && userAuth ? fetchAdminDetails() : () => { };
    return () => unsub?.();
  }, [userAuth, fetchAdminDetails, authLoading]);
  return (
    <>
    <HelmetProvider>
      <Routes>
        {/* <Route path="test" element={<Test />} /> */}
        {/* Landing Pages Routes */}
        <Route path="/" element={<LandingLayout />}>
          <Route index element={<StudentPage />} />
          {/* <Route path="studentlanding" element={<StudentPage />} /> */}
          <Route path="practido-for-schools" element={<SchoolLandingPage />} />
          <Route path="practido-for-parents" element={<ParentPage />} />
          <Route path="practido-for-educators" element={<EducatorPage />} />
          <Route path="olympiad-preparation-apps" element={<OurApps />} />
          {/* <Route path="olympiadLanding" element={<OlympiadLanding />} /> */}
          <Route path="about" element={<AboutPage />} />
          <Route path="contactus" element={<ContactLanding />} />
          <Route path="privacy" element={<PrivacyPolicy />} />
          <Route path="terms" element={<Terms />} />
          <Route path="privacy-policy.html" element={<PrivacyPolicy />} />
        </Route>
        <Route path="/" element={<DashobardLinks />}>
          <Route path="faqLanding" element={<FaqLanding />} />
          <Route path="syllabusLanding" element={<SyllabusLanding />} />
          <Route path="examLanding" element={<ExamLanding />} />
          <Route path="awardsLanding" element={<AwardsLanding />} />
          <Route path="instructions" element={<InstructionsLanding />} />
        </Route>
        {/* Registration Routes */}
        <Route
          path="register"
          element={
            <UserAuthProtected authenticate={false}>
              <RegisterLayout />
            </UserAuthProtected>
          }
        >
          {/* <Route index element={<IndividualRegisterForm />} /> */}
          <Route path="individual" element={<IndividualRegisterForm />}>
            <Route path=":schoolId" element={<IndividualRegisterForm />} />
          </Route>
          <Route path="school" element={<SchoolRegisterFormStep />}>
            <Route path="setPassword" element={<PasswordModule />} />
          </Route>
        </Route>
        {/* Login Routes */}
        <Route
          path="login"
          element={
            <UserAuthProtected authenticate={false}>
              <LoginLayout />
            </UserAuthProtected>
          }
        >
          <Route index element={<UserLogin />} />
          <Route path="school" element={<UserLogin />} />
          <Route path="individual" element={<UserLogin />} />
          <Route path="forgotPassword" element={<ForgotPassword />} />
        </Route>
        {/* Onboarding Routes */}
        <Route path="onboarding" element={<Onboarding />}>
          <Route index element={<Step1 />} />
          <Route path="step1" element={<Step1 />} />
          <Route path="step2" element={<Step3 />} />
          <Route path="success" element={<Step4 />} />
        </Route>
        {/* Dashboard Routes */}
        <Route
          path="schoolDashboard"
          element={
            <UserAuthProtected authenticate={true}>
              <SchoolDashboardLayout />
            </UserAuthProtected>
          }
        >
          <Route index element={<SchoolDashboard />} />
          <Route path="registeredStudents" element={<Outlet />}>
            <Route index element={<RegisteredStudents />} />
            <Route path=":studentId" element={<StudentLayout page="view" />} />
          </Route>
          <Route
            path="results"
            element={
              <div className="w-full h-[75vh] flex items-center justify-center">
                <h1 className="text-3xl text-gray-500 font-bold">
                  Coming Soon
                </h1>
              </div>
            }
          />
        </Route>
        <Route
          path="parentDashboard"
          element={
            <UserAuthProtected authenticate={true}>
              <ParentDashboardLayout />
            </UserAuthProtected>
          }
        >
          <Route index element={<ParentDashboard />} />
          <Route path=":studentId" element={<Outlet />}>
            <Route index element={<StudentLayout page="view" />} />
            <Route path="edit" element={<StudentLayout page="edit" />} />
            <Route
              path="addSubjects"
              element={<StudentLayout page="addSubjects" />}
            />
          </Route>
        </Route>
        {/* Content Creation Routes */}
        <Route path="contentCreation" element={<ContentCreationLayout />}>
          <Route index element={<Navigate to="questions" />} />
          <Route path="questions" element={<Outlet />}>
            <Route index element={<AllQuestions />} />
            <Route path="upload" element={<UploadQuestion />} />
            <Route path="edit/:id" element={<UploadQuestion />} />
          </Route>
          <Route path="text" element={<Outlet />}>
            <Route index element={"Text Content"} />
            <Route path="upload" element={"Upload Text Content"} />
          </Route>
          <Route path="document" element={<Outlet />}>
            <Route index element={"Document Content"} />
            <Route path="upload" element={"Upload Document Content"} />
          </Route>
          <Route path="video" element={<Outlet />}>
            <Route index element={"Video Content"} />
            <Route path="upload" element={"Upload Video Content"} />
          </Route>
        </Route>
        {/*  */}
        {/* Assessmnt Test  */}
        <Route path="/exam/login" element={<ExamLogin />} />
        <Route
          path="/exam"
          element={
            <ExamAuthProtected authenticate>
              <Outlet />
            </ExamAuthProtected>
          }
        >
          {/* <Route path="start" element={<CountDown />} /> */}
          <Route path="setup" element={<Outlet />}>
            <Route index element={<Navigate to="camera" />} />
            <Route path=":step" element={<Setup />} />
          </Route>
          <Route path="instructions" element={<ExamInstructions />} />
          <Route path="assessment" element={<ExamPage />} />
        </Route>
        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </HelmetProvider>
    </>
  );
};

export default App;
